
import { Elastic, Sine, TimelineLite, TimelineMax } from "gsap";
import * as HLF from "html-living-framework";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { GalleryQuiltData } from "../Data/GalleryQuiltData";
import { GameType } from "../Data/GameData";
import * as QuiltData from "../Data/QuiltData";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOver";
import { Button } from "../GameObjects/Button";
import { Patch } from "../GameObjects/Patch";
import { PatchPalette } from "../GameObjects/PatchPalette";
import { Quilt } from "../GameObjects/Quilt";
import { Game } from "../States/Game";
import * as QuiltUtils from "../Utils/QuiltUtils";

export class GameQuilt extends Game {
    protected readonly _gameType = GameType.QUILT;

    protected _buttonNext: Button;
    protected _buttonPrev: Button;
    protected _activeQuiltIndex: number;
    private _quilts: Quilt[];
    private _palette: PatchPalette;
    private _buttonSwap: Button;
    private _swapHintTween: TimelineMax | undefined;

    constructor() {
        super();
        this._funFactCommon = VoiceOver.FUN_FACT_QUILT_COMMON;
        this._funFactUnique = VoiceOver.FUN_FACT_QUILT_UNIQUE;
        this._activeQuiltIndex = HLF.Random.intBetween(0, QuiltData.DATA.length);
        this._currentFunFactIndex = this._activeQuiltIndex;

        this._quilts = new Array<Quilt>(QuiltData.DATA.length);
        for (let i = 0; i < this._quilts.length; ++i) {

            this._quilts[i] = new Quilt(i);
            this._quilts[i].visible = (i === this._activeQuiltIndex);
            this._quilts[i].isActive = (i === this._activeQuiltIndex);

            const data: GalleryQuiltData = AppProgress.games[this._gameType].saveData[i] as GalleryQuiltData;
            if (data !== undefined) {
                for (let j = 0; j < data.patches.length; ++j) {
                    this._quilts[i].addPatch(new Patch(data.patches[j]), data.positions[j], true);
                }
            }
            this.addChild(this._quilts[i]);
        }

        this._palette = new PatchPalette(this._activeQuiltIndex, this._isOnboarding);
        this.addChild(this._palette);

        this._buttonSwap = new Button(
            this.onButtonSwapPressed.bind(this),
            "button-swap", Constants.SPRITESHEET_UI,
            undefined,
            SoundEffect.UI_CLICK,
        );
        this._buttonSwap.interactive = false;
        this.addChild(this._buttonSwap);

        this._buttonNext = new Button(
            this.onNextPressed.bind(this),
            "button-right", Constants.SPRITESHEET_UI,
            undefined,
            SoundEffect.UI_CLICK,
        );
        this.addChild(this._buttonNext);

        this._buttonPrev = new Button(
            this.onPrevPressed.bind(this),
            "button-left", Constants.SPRITESHEET_UI,
            undefined,
            SoundEffect.UI_CLICK,
        );
        this.addChild(this._buttonPrev);

        HLF.PixiUtils.sendToFront(this._andy);
        HLF.PixiUtils.sendToFront(this._buttonClear);
        HLF.PixiUtils.sendToFront(this._buttonSubmit);
        HLF.PixiUtils.sendToFront(this._onboardingManager);
        HLF.PixiUtils.sendToFront(this._keyboardFocusManager);
    }

    public static getRequiredAssets(): string[] {
        const required: string[] = super.getRequiredAssets();
        for (let i = 0; i < QuiltUtils.COLOUR_RAMP_COUNT; ++i) {
            required.push(
                "colour-ramp-" +
                HLF.NumberUtils.addZeroPadding(i.toString(), 2) +
                HLF.ResourceUtils.FILE_EXTENSION_PNG);
        }
        for (let i = 0; i < QuiltData.DATA.length; ++i) {
            required.push(Constants.SPRITESHEET_QUILT_PREFIX + "0" + i + HLF.ResourceUtils.FILE_EXTENSION_JSON);
        }
        return required;
    }

    public onResize(pSize: PIXI.Point): void {
        super.onResize(pSize);
        if (this._buttonSwap !== undefined) {
            this._buttonSwap.position.set(
                -this._size.x * 0.5 + (this._size.x - 660) * 0.25,
                this._size.y * 0.5 - 90,
            );
            this._buttonClear.position.set(
                this._size.x * 0.5 + (this._buttonClear.interactive ? -1 : 1) * (this._buttonClear.width * 0.5 + 15),
                -241,
            );
            this._buttonNext.position.set(
                this._size.x * 0.5 + (this._buttonNext.interactive ? -1 : 1) * (this._buttonNext.width * 0.5 + 15),
                -133,
            );
            this._buttonPrev.position.set(
                this._size.x * 0.5 + (this._buttonPrev.interactive ? -1 : 1) * (this._buttonPrev.width * 0.5 + 15),
                -25,
            );
            this._buttonSubmit.position.y = 100;
            this._palette.position.set(
                this._buttonSwap.position.x,
                -80,
            );
        }
    }

    public animateIn(pOnComplete: () => void): void {
        super.animateIn(pOnComplete);

        this.setButtonsInteractive(false);
        this._palette.position.x = -this._size.x * 0.5 - this._palette.width;
        this._quilts[this._activeQuiltIndex].position.set(
            -this._size.x * 0.5 - this._quilts[this._activeQuiltIndex].width,
            0,
        );
        this._buttonSwap.position.x = -this._size.x * 0.5 - this._buttonSwap.width;
        this._buttonSubmit.position.x = this._size.x * 0.5 + this._buttonSubmit.width;
        this._buttonNext.position.x = this._buttonSubmit.position.x;
        this._buttonPrev.position.x = this._buttonSubmit.position.x;
        this._buttonClear.position.x = this._buttonSubmit.position.x;

        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._quilts[this._activeQuiltIndex].position, 1.0, {
            x: 0,
            ease: Sine.easeOut,
        }, 0);
        timeline.to(this._palette.position, 0.5, {
            x: -this._size.x * 0.5 + (this._size.x - 660) * 0.25,
            ease: Sine.easeOut,
        }, 0.5);
        if (this._isOnboarding === false) {
            timeline.to(this._buttonSwap.position, 0.5, {
                x: -this._size.x * 0.5 + (this._size.x - 660) * 0.25,
                ease: Sine.easeOut,
            }, 0.5);
            timeline.to(this._buttonClear.position, 0.5, {
                x: this._size.x * 0.5 - this._buttonClear.width * 0.5 - 15,
                ease: Sine.easeOut,
            }, 0.5);
            timeline.to(this._buttonNext.position, 0.5, {
                x: this._size.x * 0.5 - this._buttonNext.width * 0.5 - 15,
                ease: Sine.easeOut,
            }, 0.6);
            timeline.to(this._buttonPrev.position, 0.5, {
                x: this._size.x * 0.5 - this._buttonPrev.width * 0.5 - 15,
                ease: Sine.easeOut,
            }, 0.7);
            timeline.to(this._buttonSubmit.position, 0.5, {
                x: this._size.x * 0.5 - this._buttonSubmit.width * 0.5 - 15,
                ease: Sine.easeOut,
            }, 0.8);
        }

        timeline.call(this.onAnimateInComplete.bind(this));
    }

    public animateOut(pOnComplete: () => void): void {
        super.animateOut(pOnComplete);

        const timeline: TimelineLite = new TimelineLite();

        timeline.to(this._buttonClear, 0.5, {
            x: this._size.x * 0.5 + this._buttonClear.width,
            ease: Sine.easeIn,
        }, 0);
        timeline.to(this._buttonNext, 0.5, {
            x: this._size.x * 0.5 + this._buttonNext.width,
            ease: Sine.easeIn,
        }, 0.1);
        timeline.to(this._buttonPrev, 0.5, {
            x: this._size.x * 0.5 + this._buttonPrev.width,
            ease: Sine.easeIn,
        }, 0.2);
        timeline.to(this._buttonSubmit.position, 0.5, {
            x: this._size.x * 0.5 + this._buttonSubmit.width,
            ease: Sine.easeIn,
        }, 0.3);
        timeline.to(this._palette.position, 0.5, {
            x: -this._size.x * 0.5 - this._palette.width,
            ease: Sine.easeIn,
        }, 0);
        timeline.to(this._buttonSwap.position, 0.5, {
            x: -this._size.x * 0.5 - this._buttonSwap.width,
            ease: Sine.easeIn,
        }, 0);
        timeline.call(() => {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
                SoundEffect.LIFT_ART,
                1, false, HLF.AudioCategory.SFX,
                ));
        }, undefined, undefined, 0.5);
        timeline.to(this._quilts[this._activeQuiltIndex].position, 0.8, {
            x: -this._size.x * 0.5 - this._quilts[this._activeQuiltIndex].width,
            ease: Sine.easeIn,
        }, 0.5);
    }

    protected saveData(): void {
        for (let i = 0; i < this._quilts.length; ++i) {
            AppProgress.games[this._gameType].saveData[i] = this._quilts[i].generateSaveData();
        }
        AppProgress.games[this._gameType].recentItemIndex = this._activeQuiltIndex;
    }

    protected onAnimateInComplete(): void {
        super.onAnimateInComplete();
        this._palette.onAnimateInComplete();
        if (this._isOnboarding) {
            this.populateOnboarding();
        }
        else {
            this.setButtonsInteractive(true);
            PubSub.publishSync(
                HLF.REGISTER_FOCUSABLES,
                [ this._buttonClear, this._buttonNext, this._buttonPrev, this._buttonSubmit, this._buttonSwap ],
            );
        }
        PubSub.publishSync(Topics.KEYBOARD_REFOCUS, undefined);
    }

    protected onClearPressed(): void {
        this._buttonClear.interactive = false;
        this._palette.interactiveChildren = false;
        const timeline: TimelineLite = this._quilts[this._activeQuiltIndex].clear();
        timeline.call(() => {
            this._buttonClear.interactive = true;
            this._palette.interactiveChildren = true;
        });
    }

    private switchQuilt(pStep: number): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        const isIncrement: boolean = pStep > 0;

        this.setButtonsInteractive(false);
        timeline.add(this.animateQuiltOut(this._activeQuiltIndex, isIncrement), 0);
        this.incrementIndex(pStep);
        this._palette.swapPatches(this._activeQuiltIndex);
        timeline.add(this.animateQuiltIn(this._activeQuiltIndex, isIncrement), 0);
        timeline.add(() => {
            this.setButtonsInteractive(true);
        }, 1.0);

        return timeline;
    }

    private animateQuiltIn(pIndex: number, pFromRight: boolean): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        const quilt: Quilt = this._quilts[pIndex];
        quilt.visible = true;
        quilt.isActive = false;
        quilt.position.x = (this._size.x * 0.5 + quilt.width * 0.5) * (pFromRight ? 1 : -1);
        timeline.to(quilt.position, 0.75, {
            x: 0,
            ease: Sine.easeInOut,
        });
        timeline.call(() => {
            quilt.isActive = true;
        });
        return timeline;
    }

    private animateQuiltOut(pIndex: number, pToLeft: boolean): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        const quilt: Quilt = this._quilts[pIndex];
        timeline.to(quilt.position, 0.75, {
            x: (this._size.x * 0.5 + quilt.width * 0.5) * (pToLeft ? -1 : 1),
            ease: Sine.easeInOut,
        });
        timeline.call(() => {
            quilt.visible = false;
            quilt.isActive = false;
        });
        return timeline;
    }

    private incrementIndex(pStep: number): void {
        this._activeQuiltIndex += pStep;
        if (this._activeQuiltIndex >= this._quilts.length) {
            this._activeQuiltIndex = 0;
        }
        else if (this._activeQuiltIndex < 0) {
            this._activeQuiltIndex = this._quilts.length - 1;
        }
        this._currentFunFactIndex = this._activeQuiltIndex;
    }

    private setButtonsInteractive(pInteractive: boolean): void {
        this._buttonNext.interactive = pInteractive;
        this._buttonPrev.interactive = pInteractive;
        this._buttonSubmit.interactive = pInteractive;
        this._buttonSwap.interactive = pInteractive;
        this._buttonClear.interactive = pInteractive;
    }

    private populateOnboarding(): void {
        this._onboardingManager.clear();
        this._onboardingManager.addStep(
            Topics.PATCH_SELECTED,
            undefined,
            this._palette,
            2.0,
            true,
        );
        this._onboardingManager.addStep(
            Topics.PATCH_ADDED,
            undefined,
            this._quilts[this._activeQuiltIndex],
            1.5,
            true,
            Topics.PATCH_DESELECTED,
            undefined,
            this.onOnboardingComplete.bind(this),
        );
        this._onboardingManager.start();
    }

    private playSwapHint(): void {
        this.stopSwapHint();

        this._swapHintTween = new TimelineMax({
            repeat: -1,
        });
        this._swapHintTween.to(this._buttonSwap.scale, 0.15, {
            x: 1.15,
            y: 1.15,
            ease: Sine.easeInOut,
        }, 5.0);
        this._swapHintTween.to(this._buttonSwap.scale, 0.1, {
            x: 0.9,
            y: 0.9,
            ease: Sine.easeOut,
        });
        this._swapHintTween.to(this._buttonSwap.scale, 1.0, {
            x: 1.0,
            y: 1.0,
            ease: Elastic.easeOut,
        });
    }

    private stopSwapHint(): void {
        if (this._swapHintTween !== undefined) {
            this._swapHintTween.kill();
            this._buttonSwap.scale.set(1);
        }
    }

    private onButtonSwapPressed(): void {
        this._palette.swapPatches();
        PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(SoundEffect.SHUFFLE, 1, false, HLF.AudioCategory.SFX));
        this.stopSwapHint();
    }

    private onNextPressed(): void {
        this.switchQuilt(1);
        PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
            HLF.ArrayUtils.random(SoundEffect.WHOOSH),
            1, false, HLF.AudioCategory.SFX));
    }

    private onPrevPressed(): void {
        this.switchQuilt(-1);
        PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
            HLF.ArrayUtils.random(SoundEffect.WHOOSH),
            1, false, HLF.AudioCategory.SFX));
    }

    private onOnboardingComplete(): void {
        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._buttonClear.position, 0.5, {
            x: this._size.x * 0.5 - this._buttonClear.width * 0.5 - 15,
            ease: Sine.easeOut,
        }, 0);
        timeline.to(this._buttonNext.position, 0.5, {
            x: this._size.x * 0.5 - this._buttonNext.width * 0.5 - 15,
            ease: Sine.easeOut,
        }, 0.1);
        timeline.to(this._buttonPrev.position, 0.5, {
            x: this._size.x * 0.5 - this._buttonPrev.width * 0.5 - 15,
            ease: Sine.easeOut,
        }, 0.2);
        timeline.to(this._buttonSubmit.position, 0.5, {
            x: this._size.x * 0.5 - this._buttonSubmit.width * 0.5 - 15,
            ease: Sine.easeOut,
        }, 0.3);
        timeline.call(this._palette.onOnboardingComplete, undefined, this._palette, 0.2);
        timeline.to(this._buttonSwap.position, 0.5, {
            x: -this._size.x * 0.5 + (this._size.x - 660) * 0.25,
            ease: Sine.easeOut,
        }, 0.4);
        timeline.call(() => {
            this.setButtonsInteractive(true);
            this.playSwapHint();

            PubSub.publishSync(
                HLF.REGISTER_FOCUSABLES,
                [ this._buttonNext, this._buttonPrev, this._buttonSubmit, this._buttonSwap ],
            );
        });
    }
}
